import {
  EdgelessLegacySlotIdentifier,
  OverlayIdentifier,
} from '@blocksuite/affine-block-surface';
import {
  DefaultTheme,
  type FrameBlockModel,
  FrameBlockSchema,
  isTransparent,
} from '@blocksuite/affine-model';
import {
  EditPropsStore,
  ThemeProvider,
} from '@blocksuite/affine-shared/services';
import { getRenderXYWH } from '@blocksuite/affine-shared/utils';
import { Bound } from '@blocksuite/global/gfx';
import { GfxBlockComponent } from '@blocksuite/std';
import {
  type BoxSelectionContext,
  getTopElements,
  type GfxModel,
  GfxViewInteractionExtension,
} from '@blocksuite/std/gfx';
import { cssVarV2 } from '@streamlain/theme/v2';
import { css, html } from 'lit';
import { state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';

import {
  EdgelessFrameManagerIdentifier,
  type FrameOverlay,
} from './frame-manager';

/**
 * Awareness state type for presenter detection
 */
interface PresentationAwarenessState {
  presentation?: {
    isPresenting: boolean;
    mode: 'free' | 'frames';
  };
  viewport?: {
    frameId?: string;
  };
}

export class FrameBlockComponent extends GfxBlockComponent<FrameBlockModel> {
  static override styles = css`
    @keyframes presenter-frame-pulse {
      0%,
      100% {
        box-shadow: 0 0 15px 5px var(--streamlain-brand-color);
        opacity: 0.7;
      }
      50% {
        box-shadow: 0 0 25px 10px var(--streamlain-brand-color);
        opacity: 1;
      }
    }
  `;

  override connectedCallback() {
    super.connectedCallback();

    this._disposables.add(
      this.store.slots.blockUpdated.subscribe(({ type, id }) => {
        if (id === this.model.id && type === 'update') {
          this.requestUpdate();
        }
      })
    );
    // Track selection state for border visibility
    this._disposables.add(
      this.gfx.selection.slots.updated.subscribe(() => {
        const isFrameSelected = this.gfx.selection.has(this.model.id);
        const hasSelectedDescendant = this.gfx.selection.selectedIds.some(
          id => {
            const element = this.gfx.getElementById(id);
            return element
              ? this.model.hasDescendant(element as GfxModel)
              : false;
          }
        );
        this._isSelected = isFrameSelected || hasSelectedDescendant;
      })
    );

    // Listen to show frames setting
    const slots = this.std.getOptional(EdgelessLegacySlotIdentifier);
    if (slots) {
      this._disposables.add(
        slots.navigatorSettingUpdated.subscribe(({ showFramesOnCanvas }) => {
          if (showFramesOnCanvas !== undefined) {
            this._showFramesOnCanvas = showFramesOnCanvas;
          }
        })
      );
    }

    // Restore setting from storage
    const editPropsStore = this.std.getOptional(EditPropsStore);
    if (editPropsStore) {
      const stored = editPropsStore.getStorage('edgelessShowFrames');
      this._showFramesOnCanvas = typeof stored === 'boolean' ? stored : true;
    }

    // Listen to awareness changes for presenter frame highlight
    this._setupPresenterFrameListener();
  }

  /**
   * Setup awareness listener to detect if this frame is being presented.
   * Shows a pulsing glow for non-followers when presenter is on this frame.
   */
  private _setupPresenterFrameListener(): void {
    const awareness = this.store.awarenessStore.awareness;

    const handler = () => this._updatePresenterFrameState();
    awareness.on('change', handler);
    this._disposables.add(() => awareness.off('change', handler));

    // Also listen to fullscreen changes to hide/show highlight
    const fullscreenHandler = () => this._updatePresenterFrameState();
    document.addEventListener('fullscreenchange', fullscreenHandler);
    this._disposables.add(() =>
      document.removeEventListener('fullscreenchange', fullscreenHandler)
    );

    // Initial check
    this._updatePresenterFrameState();
  }

  /**
   * Check if a presenter is currently showing this frame.
   * Updates _isPresenterFrame state to trigger glow effect.
   *
   * Only shows glow when:
   * - Presenter is in frames mode
   * - This frame is being presented
   * - User is NOT in fullscreen (exploring freely)
   */
  private _updatePresenterFrameState(): void {
    // Don't show highlight if user is in fullscreen (they're following the presentation)
    if (document.fullscreenElement) {
      if (this._isPresenterFrame) {
        this._isPresenterFrame = false;
      }
      return;
    }

    const awareness = this.store.awarenessStore.awareness;
    const states = awareness.getStates();
    const myClientId = awareness.clientID;

    let presenterFrameId: string | null = null;

    for (const [clientId, state] of states) {
      // Skip self - don't highlight my own presentation
      if (clientId === myClientId) continue;

      const typedState = state as PresentationAwarenessState;

      // Check if this client is presenting in frames mode
      if (
        typedState.presentation?.isPresenting &&
        typedState.presentation.mode === 'frames' &&
        typedState.viewport?.frameId
      ) {
        presenterFrameId = typedState.viewport.frameId;
        break; // Support single presenter for now
      }
    }

    // Update state only if changed
    const isPresenterFrame = presenterFrameId === this.model.id;
    if (this._isPresenterFrame !== isPresenterFrame) {
      console.log('[FrameBlock] Glow state changed:', {
        frameId: this.model.id,
        presenterFrameId,
        isPresenterFrame,
        isFullscreen: !!document.fullscreenElement,
      });
      this._isPresenterFrame = isPresenterFrame;
    }
  }

  /**
   * Due to potentially very large frame sizes, CSS scaling can cause iOS Safari to crash.
   * To mitigate this issue, we combine size calculations within the rendering rect.
   */
  override getCSSTransform(): string {
    return '';
  }

  override getRenderingRect() {
    const viewport = this.gfx.viewport;
    const { translateX, translateY, zoom } = viewport;
    const { rotate } = this.model;
    // Remote awareness previews are render-only until the final drop commits.
    const bound = Bound.deserialize(getRenderXYWH(this.model));

    const scaledX = bound.x * zoom + translateX;
    const scaledY = bound.y * zoom + translateY;

    return {
      x: scaledX,
      y: scaledY,
      w: bound.w * zoom,
      h: bound.h * zoom,
      rotate,
      zIndex: this.toZIndex(),
    };
  }

  override onBoxSelected(context: BoxSelectionContext) {
    const { box } = context;
    const bound = new Bound(box.x, box.y, box.w, box.h);
    const elementBound = this.model.elementBound;

    return (
      this.model.childElements.length === 0 || bound.contains(elementBound)
    );
  }

  override renderGfxBlock() {
    const { model, showBorder, std, _showFramesOnCanvas, _isSelected } = this;
    const _isNavigator =
      this.gfx.tool.currentToolName$.value === 'frameNavigator';

    // Frame border is visible when:
    // 1. Toggle is ON (_showFramesOnCanvas)
    // 2. Frame or child element is selected (_isSelected)
    // 3. Presentation mode (_isNavigator)
    const shouldShowBorder =
      showBorder && (_showFramesOnCanvas || _isSelected || _isNavigator);

    const backgroundColor = std
      .get(ThemeProvider)
      .generateColorProperty(model.props.background, DefaultTheme.transparent);
    const frameIndex = this.gfx.layer.getZIndex(model);

    const widgets = html`${repeat(
      Object.entries(this.widgets),
      ([id]) => id,
      ([_, widget]) => widget
    )}`;

    return html`
      <div
        class="affine-frame-container"
        style=${styleMap({
          zIndex: `${frameIndex}`,
          backgroundColor,
          height: '100%',
          width: '100%',
          borderRadius: '2px',
          transition: 'box-shadow 0.3s ease, border 0.2s ease',
          border: shouldShowBorder
            ? `1px solid ${cssVarV2('edgeless/frame/border/default')}`
            : 'none',
          // Presenter frame highlight - pulsing glow for non-followers
          boxShadow: this._isPresenterFrame
            ? '0 0 20px 8px var(--streamlain-brand-color)'
            : 'none',
          animation: this._isPresenterFrame
            ? 'presenter-frame-pulse 2s ease-in-out infinite'
            : 'none',
        })}
      ></div>
      ${widgets}
    `;
  }

  @state()
  private accessor _isPresenterFrame = false;

  @state()
  private accessor _isSelected = false;

  @state()
  private accessor _showFramesOnCanvas = true;

  @state()
  accessor showBorder = true;
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-frame': FrameBlockComponent;
  }
}

export const FrameBlockInteraction =
  GfxViewInteractionExtension<FrameBlockComponent>(
    FrameBlockSchema.model.flavour,
    {
      handleResize: context => {
        const { model, std } = context;

        return {
          onResizeStart(context): void {
            context.default(context);
            model.stash('childElementIds');
          },

          onResizeMove(context): void {
            const { newBound } = context;
            const frameManager = std.getOptional(
              EdgelessFrameManagerIdentifier
            );
            const overlay = std.getOptional(
              OverlayIdentifier('frame')
            ) as FrameOverlay;

            model.xywh = newBound.serialize();

            if (!frameManager) {
              return;
            }

            const oldChildren = frameManager.getChildElementsInFrame(model);

            const newChildren = getTopElements(
              frameManager.getElementsInFrameBound(model)
            ).concat(
              oldChildren.filter(oldChild => {
                return model.intersectsBound(oldChild.elementBound);
              })
            );

            frameManager.removeAllChildrenFromFrame(model);
            frameManager.addElementsToFrame(model, newChildren);

            overlay?.highlight(model, true, false);
          },
          onResizeEnd(context): void {
            context.default(context);
            model.pop('childElementIds');
          },
        };
      },
      handleRotate: () => {
        return {
          beforeRotate(context): void {
            context.set({
              rotatable: false,
            });
          },
        };
      },
      handleSelection: () => {
        return {
          selectable(context) {
            const { model } = context;

            const onTitle =
              model.externalBound?.containsPoint([
                context.position.x,
                context.position.y,
              ]) ?? false;

            return (
              context.default(context) &&
              (model.isLocked() ||
                !isTransparent(model.props.background) ||
                onTitle)
            );
          },
          onSelect(context) {
            return context.default(context);
          },
        };
      },
    }
  );
